// Yaka kartı toplu PDF üretimi: şablonun ön yüzüne isim yazar, arkasına programı olduğu gibi ekler.
import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { read, utils } from "xlsx";

// Şablon PDF'te her kategorinin ön yüzü `page` numaralı sayfadadır, arka yüzü hemen sonraki sayfa.
// light: açık zeminli kart (yazı lacivert), değilse koyu zemin (yazı beyaz).
export const YAKA_KATEGORILER = [
  { id: "katilimci", label: "Katılımcı", page: 0 },
  { id: "konusmaci", label: "Konuşmacı", page: 1, titles: true },
  { id: "moderator", label: "Moderatör", page: 2, titles: true },
  { id: "gorevli", label: "Görevli", page: 3, light: true },
  { id: "fuaye", label: "Fuaye", page: 4, light: true },
];

const BASE = process.env.PUBLIC_URL + "/yaka/";
const LEFT = 28; // logo ve kategori yazısıyla aynı sol hiza
// Yerleşim (pt, kartın üstünden). Ünvanlı kartta isim biraz aşağı iner, ünvan üstüne yazılır.
const NAME_BASELINE = 172;
const NAME_BASELINE_WITH_TITLE = 187;
const TITLE_BASELINE = 156;
const NAME_SIZE = 26;
const TITLE_SIZE = 13;
const DARK_COLORS = { name: rgb(1, 1, 1), title: rgb(0.86, 0.92, 1) };
const LIGHT_COLORS = { name: rgb(0.04, 0.11, 0.31), title: rgb(0.1, 0.22, 0.5) };

const clean = (s) => String(s ?? "").replace(/\s+/g, " ").trim();
const lowerTr = (s) => s.toLocaleLowerCase("tr-TR");
const upperTr = (s) => s.toLocaleUpperCase("tr-TR");
// Tamamı büyük ya da tamamı küçük yazılmış isimleri "Ad Soyad" biçimine çevirir; karışık yazılmışlara dokunmaz.
const properName = (n) => {
  if (n !== upperTr(n) && n !== lowerTr(n)) return n;
  return lowerTr(n)
    .split(" ")
    .map((w) => upperTr(w.slice(0, 1)) + w.slice(1))
    .join(" ");
};
// Son kelime (soyad) büyük harfle: "Ayşe Yılmaz" -> "Ayşe YILMAZ". Tek kelimelik isme dokunulmaz.
const surnameUpper = (n) => {
  const i = n.lastIndexOf(" ");
  return i < 0 ? n : n.slice(0, i + 1) + upperTr(n.slice(i + 1));
};
const formatName = (n) => surnameUpper(properName(clean(n)));
// Baştaki ünvan kelimeleri: "Prof. Dr. Erdal Arıkan" -> ünvan "Prof. Dr.", isim "Erdal Arıkan"
const TITLE_WORD = /^(prof|doç|dr|yrd|öğr|gör|arş|uzm|av|müh|op|dyt|psk|ecz|dt|üyesi|uzmanı)\.?$/i;
const splitLeadingTitle = (full) => {
  const words = clean(full).split(" ");
  let i = 0;
  while (i < words.length - 1 && TITLE_WORD.test(words[i])) i += 1;
  return { title: words.slice(0, i).join(" "), name: words.slice(i).join(" ") };
};
// Satır biçimi: "Ad Soyad", "Prof. Dr. Ad Soyad" ya da açık biçim "Prof. Dr. | Ad Soyad".
// withTitles: ünvan ayrı satıra alınır; değilse ünvan isimle birlikte tek satırda kalır.
export const parseEntry = (line, withTitles = true) => {
  const i = line.indexOf("|");
  let { title, name } =
    i >= 0
      ? { title: clean(line.slice(0, i)), name: clean(line.slice(i + 1)) }
      : withTitles
      ? splitLeadingTitle(line)
      : { title: "", name: clean(line) };
  if (!withTitles && title) {
    name = `${title} ${name}`;
    title = "";
  }
  return { title: title ? properName(title) : "", name: formatName(name) };
};

const load = (name) =>
  fetch(BASE + name).then((r) => {
    if (!r.ok) throw new Error(name + " yüklenemedi");
    return r.arrayBuffer();
  });

// Excel'den isim listesi: "Ad Soyad" / "İsim" / "ADI-SOYADI" kolonu, ya da "Ad" + "Soyad" kolonları,
// bulunamazsa ilk kolon. Başlık satırı otomatik atlanır.
export const readNamesFromExcel = async (file) => {
  const wb = read(await file.arrayBuffer());
  const rows = utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: "" });
  if (!rows.length) return [];
  const head = rows[0].map((h) => clean(h).toLocaleLowerCase("tr-TR"));
  const idx = (re) => head.findIndex((h) => re.test(h));

  let getName;
  let start = 0;
  const full = idx(/(ad.?soyad|isim|name)/);
  const ad = idx(/^ad[ıi]?$/);
  const soyad = idx(/^soyad[ıi]?$/);
  if (full >= 0) {
    getName = (r) => r[full];
    start = 1;
  } else if (ad >= 0 && soyad >= 0) {
    getName = (r) => `${r[ad]} ${r[soyad]}`;
    start = 1;
  } else {
    getName = (r) => r[0];
    start = /^(ad|isim|ad.?soyad|adı)/.test(head[0]) ? 1 : 0;
  }
  const unvan = idx(/^(ünvan|unvan|unvanı|ünvanı|title)$/);
  return rows
    .slice(start)
    .map((r) => {
      const name = clean(getName(r));
      const title = unvan >= 0 ? clean(r[unvan]) : "";
      return name && title ? `${title} | ${name}` : name;
    })
    .filter(Boolean);
};

// Bütün kartlarda aynı punto: sığmayan isim küçültülmez, kelime sınırından satırlara bölünür.
// (Tek bir kelime bile sığmayacak kadar uzunsa, sadece o durumda küçülür.)
const layoutName = (name, font, maxW) => {
  const w = (t) => font.widthOfTextAtSize(t, NAME_SIZE);
  const lines = [];
  let cur = "";
  name.split(" ").forEach((word) => {
    const next = cur ? `${cur} ${word}` : word;
    if (cur && w(next) > maxW) {
      lines.push(cur);
      cur = word;
    } else {
      cur = next;
    }
  });
  lines.push(cur);
  const widest = Math.max(...lines.map(w));
  const size = widest > maxW ? (NAME_SIZE * maxW) / widest : NAME_SIZE;
  return { size, lines };
};

/**
 * @param {string[]} entries  "Ad Soyad" ya da "Ünvan | Ad Soyad"
 * @param {number} categoryIndex  YAKA_KATEGORILER içindeki sıra
 * @returns {Promise<Uint8Array>}
 */
export const buildYakaKartiPdf = async (entries, categoryIndex) => {
  const cat = YAKA_KATEGORILER[categoryIndex];
  const [templateBytes, boldBytes, mediumBytes] = await Promise.all([
    load("sablon.pdf"),
    load("Montserrat-Bold.ttf"),
    load("Montserrat-Medium.ttf"),
  ]);

  const pdf = await PDFDocument.create();
  pdf.registerFontkit(fontkit);
  const bold = await pdf.embedFont(boldBytes, { subset: true });
  const medium = await pdf.embedFont(mediumBytes, { subset: true });
  const [front, back] = await pdf.embedPdf(templateBytes, [cat.page * 2, cat.page * 2 + 1]);
  const { width: W, height: H } = front;
  const maxW = W - 2 * LEFT;
  const colors = cat.light ? LIGHT_COLORS : DARK_COLORS;

  for (const entry of entries) {
    const { title, name } = parseEntry(entry, !!cat.titles);
    const hasTitle = !!title;
    const { size, lines } = layoutName(name, bold, maxW);
    const page = pdf.addPage([W, H]);
    page.drawPage(front);
    if (hasTitle) {
      page.drawText(title, { x: LEFT, y: H - TITLE_BASELINE, size: TITLE_SIZE, font: medium, color: colors.title });
    }
    const base = hasTitle ? NAME_BASELINE_WITH_TITLE : NAME_BASELINE;
    lines.forEach((line, i) => {
      page.drawText(line, { x: LEFT, y: H - base - i * size * 1.15, size, font: bold, color: colors.name });
    });
    pdf.addPage([W, H]).drawPage(back);
  }
  return pdf.save();
};

export const downloadYakaKartiPdf = async (names, categoryIndex) => {
  const bytes = await buildYakaKartiPdf(names, categoryIndex);
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `XII_YakaKarti_${YAKA_KATEGORILER[categoryIndex].label}_${names.length}kisi.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
